// framework/client/ui/VoiceSelector.js
import { ComponentBase } from "../core/ComponentBase.js";

export class VoiceSelector extends ComponentBase {
    static styles = `
        .voice-selector-button-wrapper { display: flex; align-items: center; gap: 0.5rem; }
        .voice-selector-modal .modal-dialog { max-width: 700px; }
        .voice-selector-modal { z-index: var(--ab-z-modal); }
        .voice-selector-modal .modal-body { padding: 0; }
        .voice-selector-controls {
            padding: 0.75rem;
            border-bottom: 1px solid var(--bs-border-color);
            display: grid;
            grid-template-columns: 1fr auto auto auto;
            gap: 0.75rem;
            align-items: center;
        }
        .voice-list { max-height: 50vh; overflow-y: auto; padding: 0; margin: 0; list-style: none; }
        .voice-item {
            padding: 0.75rem 1rem;
            cursor: pointer;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            display: flex; align-items: center; gap: 0.75rem;
        }
        .voice-item:last-child { border-bottom: none; }
        .voice-item:hover { background-color: var(--bs-tertiary-bg); }
        .voice-item.selected { background-color: var(--bs-primary-bg-subtle); color: var(--bs-primary-text-emphasis); }
        .voice-item-details { flex-grow: 1; }
        .voice-item-name { font-weight: 500; }
        .voice-item-description { font-size: var(--ab-text-sm); color: var(--bs-secondary-color); }
        .voice-item-tags { font-size: var(--ab-text-xs); margin-top: 0.25rem; }
        .voice-item-tags .badge { margin-right: 0.25rem; }
        .voice-preview-btn { flex-shrink: 0; }
    `;

    constructor(config) {
        super(config);
        this.allVoices = [];
        this.filteredVoices = [];
        this.availableProviders = [];
        this.selectedValue = null;
        this.searchTerm = '';
        this.genderFilter = 'all';
        this.ageFilter = 'all';
        this.providerFilter = 'all';

        // New: Constraints passed from parent
        this.constraints = {};

        this.audioPlayer = new Audio();
        this.modalElement = null;
        this.modalInstance = null;
        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        await this.loadVoices();
    }

    /**
     * Set constraints to restrict the voice list.
     * @param {Object} constraints 
     * @param {string} constraints.provider - Only show voices from this provider
     * @param {string} constraints.modelId - Only show voices from this specific model
     * @param {boolean} constraints.isRealtime - Only show voices from Realtime models
     * @param {boolean} constraints.isTts - Only show voices from TTS models
     */
    setConstraints(constraints = {}) {
        this.constraints = constraints;
        // Reset internal filters if they conflict, or just re-render
        this.filterAndRender();
    }

    async loadVoices() {
        try {
            this.allVoices = await this.appClient.rpcClient.call('VoiceSelectorRpc.getVoices');
            const providers = new Set(this.allVoices.map(v => v.provider));
            this.availableProviders = Array.from(providers);
            this.filterAndRender();
        } catch (error) {
            this.log.error("Failed to load voices:", error);
        }
    }

    filterAndRender() {
        this.filteredVoices = this.allVoices.filter(voice => {
            // 1. Apply Hard Constraints (from Parent)
            if (this.constraints.provider && voice.provider !== this.constraints.provider) return false;
            if (this.constraints.modelId && voice.modelId !== this.constraints.modelId) return false;
            if (this.constraints.isRealtime === true && !voice.isRealtime) return false;
            if (this.constraints.isTts === true && !voice.isTts) return false;

            // FIX: Apply Streaming Constraint
            if (this.constraints.isStreaming === true && !voice.isStreaming) return false;

            // 2. Apply User Filters (from Modal UI)
            const s = this.searchTerm.toLowerCase();
            const matchesSearch = s ? voice.label.toLowerCase().includes(s) || voice.description.toLowerCase().includes(s) : true;
            const matchesGender = this.genderFilter === 'all' ? true : voice.gender === this.genderFilter;
            const matchesAge = this.ageFilter === 'all' ? true : voice.age === this.ageFilter;

            const matchesProvider = (this.providerFilter === 'all' || this.constraints.provider) ? true : voice.provider === this.providerFilter;

            return matchesSearch && matchesGender && matchesAge && matchesProvider;
        });

        if (this.modalElement) {
            this.renderVoiceList();
            this.updateModalControls();
        }
    }

    updateModalControls() {
        // Hide provider dropdown if provider is constrained
        const providerSelect = this.modalElement.querySelector('select[data-filter="provider"]');
        if (providerSelect) {
            providerSelect.style.display = this.constraints.provider ? 'none' : 'block';
        }
    }

    // ... (handlePreview, _createModalStructure, show, hide, render, _updateButtonLabel remain mostly the same) ...

    async handlePreview(e, voiceId) {
        e.stopPropagation();
        const previewBtn = e.currentTarget;
        if (previewBtn.disabled) return;

        this.audioPlayer.pause();
        this.audioPlayer.currentTime = 0;

        this.modalElement.querySelectorAll('.voice-preview-btn').forEach(btn => {
            if (btn !== previewBtn) {
                btn.disabled = false;
                btn.innerHTML = `<i class="bi bi-play-circle"></i>`;
            }
        });

        previewBtn.disabled = true;
        previewBtn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status"></span>`;

        const resetBtn = () => {
            previewBtn.disabled = false;
            previewBtn.innerHTML = `<i class="bi bi-play-circle"></i>`;
            this.audioPlayer.onended = null;
            this.audioPlayer.onerror = null;
        };
        this.audioPlayer.onended = resetBtn;
        this.audioPlayer.onerror = () => {
            this.appClient.notifier.error("Failed to play audio preview.");
            resetBtn();
        };

        try {
            const { relativePath } = await this.appClient.rpcClient.call('VoiceSelectorRpc.getVoiceSample', { voiceId: voiceId });
            this.audioPlayer.src = `/media/${relativePath}`;
            this.audioPlayer.play();
        } catch (error) {
            this.appClient.notifier.error(`Could not get preview: ${error.message}`);
            resetBtn();
        }
    }

    _createModalStructure() {
        if (this.modalElement) return;

        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade voice-selector-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');

        const providerOptions = this.availableProviders.map(p => `<option value="${p}">${p}</option>`).join('');

        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${this.t('Select a Voice')}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="voice-selector-controls">
                            <input type="search" class="form-control form-control-sm" placeholder="Search voices...">
                            <select class="form-select form-select-sm" data-filter="gender">
                                <option value="all">All Genders</option><option value="male">Male</option><option value="female">Female</option>
                            </select>
                            <select class="form-select form-select-sm" data-filter="age">
                                <option value="all">All Ages</option><option value="young">Young</option><option value="adult">Adult</option><option value="old">Old</option>
                            </select>
                            <select class="form-select form-select-sm" data-filter="provider">
                                <option value="all">All Providers</option>${providerOptions}
                            </select>
                        </div>
                        <ul class="voice-list"></ul>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);

        this.modalElement.querySelector('input[type="search"]').addEventListener('input', (e) => {
            this.searchTerm = e.target.value;
            this.filterAndRender();
        });
        this.modalElement.querySelector('select[data-filter="gender"]').addEventListener('change', (e) => {
            this.genderFilter = e.target.value;
            this.filterAndRender();
        });
        this.modalElement.querySelector('select[data-filter="age"]').addEventListener('change', (e) => {
            this.ageFilter = e.target.value;
            this.filterAndRender();
        });
        this.modalElement.querySelector('select[data-filter="provider"]').addEventListener('change', (e) => {
            this.providerFilter = e.target.value;
            this.filterAndRender();
        });
    }

    show() {
        this._createModalStructure();
        this.filterAndRender(); // Applies constraints and filters
        this.modalInstance.show();
    }

    hide() {
        this.modalInstance?.hide();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'voice-selector-button-wrapper';
            this.container.innerHTML = `
                <button type="button" class="btn btn-outline-secondary select-voice-btn w-100 text-start"></button>
            `;
            this.selectBtn = this.container.querySelector('.select-voice-btn');
            this.selectBtn.addEventListener('click', () => this.show());
        }
        this._updateButtonLabel();
        return this.container;
    }

    _updateButtonLabel() {
        if (!this.selectBtn) return;
        const selectedVoice = this.allVoices.find(v => v.value === this.selectedValue);
        if (selectedVoice) {
            this.selectBtn.innerHTML = `<i class="bi bi-mic-fill me-2"></i> ${selectedVoice.label}`;
        } else {
            this.selectBtn.innerHTML = `<i class="bi bi-mic me-2"></i> ${this.t('Select a Voice...')}`;
        }
    }

    renderVoiceList() {
        const listContainer = this.modalElement.querySelector('.voice-list');
        if (!listContainer) return;
        listContainer.innerHTML = '';

        if (this.filteredVoices.length === 0) {
            listContainer.innerHTML = `<li class="p-3 text-muted text-center">${this.t('No voices match your criteria.')}</li>`;
            return;
        }

        this.filteredVoices.forEach(voice => {
            const li = document.createElement('li');
            li.className = 'voice-item';
            if (voice.value === this.selectedValue) li.classList.add('selected');
            li.dataset.value = voice.value;

            // Logic to disable preview for Realtime voices
            const canPreview = !voice.isRealtime;
            const previewIcon = canPreview ? 'bi-play-circle' : 'bi-soundwave';
            const previewTitle = canPreview ? 'Preview Voice' : 'Realtime Voice (No Preview)';
            const disabledAttr = canPreview ? '' : 'disabled';
            const btnClass = canPreview ? 'btn-outline-secondary' : 'btn-outline-secondary opacity-50';

            li.innerHTML = `
                <div class="voice-item-details">
                    <div class="voice-item-name">
                        ${voice.label}
                        ${voice.isRealtime ? '<span class="badge bg-warning text-dark ms-2" style="font-size: var(--ab-text-2xs)">Realtime</span>' : ''}
                    </div>
                    <div class="voice-item-description">${voice.description}</div>
                    <div class="voice-item-tags">
                        <span class="badge text-bg-info">${voice.gender}</span>
                        <span class="badge text-bg-secondary">${voice.accent}</span>
                        <span class="badge text-bg-light text-dark">${voice.age || 'adult'}</span>
                    </div>
                </div>
                <button type="button" class="btn btn-sm ${btnClass} voice-preview-btn" title="${previewTitle}" ${disabledAttr}>
                    <i class="bi ${previewIcon}"></i>
                </button>
            `;

            if (canPreview) {
                li.querySelector('.voice-preview-btn').addEventListener('click', (e) => this.handlePreview(e, voice.value));
            }
            
            li.addEventListener('click', () => {
                this.setValue(voice.value);
                this.hide();
                this.container.dispatchEvent(new Event('change', { bubbles: true }));
            });

            listContainer.appendChild(li);
        });
    }
    
    getValue() { return this.selectedValue; }

    setValue(value) {
        this.selectedValue = value;
        this._updateButtonLabel();
        if (this.modalElement) {
            this.modalElement.querySelectorAll('.voice-item').forEach(item => {
                item.classList.toggle('selected', item.dataset.value === value);
            });
        }
    }

    destroy() {
        this.modalInstance?.dispose();
        this.modalElement?.remove();
        super.destroy();
    }
}